import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { RefreshIcon, Tooltip } from '@playerbase/ui';
import type { ChannelSnapshotStatus, ThemeListItem } from '@playerbase/types';

import { useShell } from '../shell/use-shell.js';
import { InsightsStatusBanner } from '../shell/job-status-banners.js';
import {
  MetricChip,
  ThemesList,
  fetchChannelSnapshots,
  fetchGameInsightsSummary,
  fetchGameThemes,
  relativeTime,
  requestChannelSnapshot,
  requestNonDiscordSnapshot,
  type GameInsightsSummary,
} from '../insights/index.js';

// Insights page. Pattern cards driven by three reads:
//   - get_game_themes      : the pattern list itself
//   - get_game_message_count: jsonb summary for the headline counts
//   - get_game_channel_snapshots: per-channel status for last refresh /
//                                 next due / refresh-now fan-out
// All three live under migration 0076 + 0078-0081.
//
// Realtime: the shell owns the single insights:<gameId> broadcast
// subscription (use-job-activity.ts) and exposes insightsVersion. When the
// bot starts (running) or finishes (done/dead) a snapshot the 0076/0117
// trigger broadcasts and the version bumps, causing the page to refetch. The
// Refresh icon click forces a refetch on resolve so in-flight state lands
// without waiting on the broadcast.

const EMPTY_SUMMARY: GameInsightsSummary = {
  chatMessageCount: 0,
  chatChannelCount: 0,
  formSubmissionCount: 0,
  totalMessageCount: 0,
};

// Canonical bot-side snapshot cadence is 6h (SNAPSHOT_CRON_INTERVAL_MS in
// apps/bot/src/ai/snapshot/constants.ts). We hardcode it here because
// the portal doesn't share that constant; if the bot's cadence changes
// this needs to move with it.
const SNAPSHOT_CADENCE_MS = 6 * 60 * 60 * 1000;

function compactDuration(ms: number): string {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remMinutes = minutes - hours * 60;
  if (hours < 24) {
    return remMinutes === 0 ? `${hours}h` : `${hours}h ${remMinutes}m`;
  }
  const days = Math.floor(hours / 24);
  const remHours = hours - days * 24;
  return remHours === 0 ? `${days}d` : `${days}d ${remHours}h`;
}

function latestRefreshAt(channels: ChannelSnapshotStatus[]): string | null {
  let max: string | null = null;
  for (const c of channels) {
    if (c.lastDoneAt === null) continue;
    if (max === null || c.lastDoneAt > max) max = c.lastDoneAt;
  }
  return max;
}

// Earliest "next due" timestamp across watched channels. A channel that
// has never been snapshotted is due now (returns now). If no watched
// channels, returns null and the caller hides the line.
function soonestNextDueAt(
  channels: ChannelSnapshotStatus[],
  now: number,
): number | null {
  if (channels.length === 0) return null;
  let soonest: number | null = null;
  for (const c of channels) {
    if (c.lastDoneAt === null) return now;
    const due = new Date(c.lastDoneAt).getTime() + SNAPSHOT_CADENCE_MS;
    if (soonest === null || due < soonest) soonest = due;
  }
  return soonest;
}

// "A, B and C" style join with no Oxford comma. We interleave at render
// time so each item can remain a JSX node (carrying its own MetricChip).
function joinPhrases(items: JSX.Element[]): JSX.Element {
  if (items.length === 0) return <></>;
  if (items.length === 1) return items[0]!;
  if (items.length === 2) {
    return (
      <>
        {items[0]} and {items[1]}
      </>
    );
  }
  const head = items.slice(0, -1);
  const tail = items[items.length - 1]!;
  return (
    <>
      {head.map((el, i) => (
        <span key={`hp-${i}`}>
          {el}
          {i < head.length - 1 ? ', ' : ''}
        </span>
      ))}{' '}
      and {tail}
    </>
  );
}

// Rich hover body for a headline chip: a bold title line over a muted
// description. Wraps the chip itself as the trigger so hovering the box
// reveals what the number counts. `cursor-help` hints the chip is more
// than decoration.
function MetricTooltip({
  title,
  body,
  children,
}: {
  title: string;
  body: ReactNode;
  children: ReactNode;
}): JSX.Element {
  return (
    <Tooltip
      multiline
      className="cursor-help"
      content={
        <span className="block">
          <span className="block font-medium text-text">{title}</span>
          <span className="mt-0.5 block text-text-muted">{body}</span>
        </span>
      }
    >
      {children}
    </Tooltip>
  );
}

interface PatternsHeadlineProps {
  patternCount: number;
  summary: GameInsightsSummary;
  channels: ChannelSnapshotStatus[];
}

function PatternsHeadline({
  patternCount,
  summary,
  channels,
}: PatternsHeadlineProps): JSX.Element {
  const watchedNames = channels
    .map((c) => c.channelName)
    .filter((name): name is string => name !== null && name !== '');

  const phrases: JSX.Element[] = [];
  if (summary.chatMessageCount > 0) {
    phrases.push(
      <span key="msgs">
        <MetricTooltip
          title="Messages"
          body="Discord messages in watched channels that carried game feedback worth grouping into patterns."
        >
          <MetricChip>{summary.chatMessageCount.toLocaleString()}</MetricChip>
        </MetricTooltip>{' '}
        message{summary.chatMessageCount === 1 ? '' : 's'}
      </span>,
    );
  }
  if (summary.chatChannelCount > 0) {
    phrases.push(
      <span key="chs">
        <MetricTooltip
          title="Watched channels"
          body={
            watchedNames.length > 0 ? (
              <>
                Discord channels being watched for feedback:{' '}
                {watchedNames.map((n) => `#${n}`).join(', ')}.
              </>
            ) : (
              'Discord channels being watched for feedback.'
            )
          }
        >
          <MetricChip>{summary.chatChannelCount}</MetricChip>
        </MetricTooltip>{' '}
        channel{summary.chatChannelCount === 1 ? '' : 's'}
      </span>,
    );
  }
  if (summary.formSubmissionCount > 0) {
    phrases.push(
      <span key="frm">
        <MetricTooltip
          title="Form submissions"
          body="Responses players send through your playtest and feedback forms."
        >
          <MetricChip>{summary.formSubmissionCount.toLocaleString()}</MetricChip>
        </MetricTooltip>{' '}
        form submission{summary.formSubmissionCount === 1 ? '' : 's'}
      </span>,
    );
  }
  return (
    <>
      <MetricTooltip
        title="Patterns"
        body="Recurring themes grouped from separate pieces of feedback, so you can see what players keep raising."
      >
        <MetricChip>{patternCount}</MetricChip>
      </MetricTooltip>{' '}
      pattern
      {patternCount === 1 ? '' : 's'} detected
      {phrases.length > 0 && <> across {joinPhrases(phrases)}</>}
    </>
  );
}

export function InsightsPage(): JSX.Element {
  const { activeStudio, activeGame, insightsVersion } = useShell();
  const gameId = activeGame?.id ?? null;

  const [themes, setThemes] = useState<ThemeListItem[]>([]);
  const [channels, setChannels] = useState<ChannelSnapshotStatus[]>([]);
  const [summary, setSummary] = useState<GameInsightsSummary>(EMPTY_SUMMARY);
  const [loaded, setLoaded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  // Local tick so the relative "last refresh / next due" labels move
  // forward without external help. 30s matches the cadence used on the
  // per-channel cards; cheap.
  const [, setTick] = useState(0);

  // The shell owns the single insights:<gameId> subscription; the page just
  // refetches when its version ticks.
  const version = insightsVersion;

  const refresh = useCallback(async () => {
    if (gameId === null) return;
    const [t, c, s] = await Promise.all([
      fetchGameThemes(gameId),
      fetchChannelSnapshots(gameId),
      fetchGameInsightsSummary(gameId),
    ]);
    setThemes(t);
    setChannels(c);
    setSummary(s);
    setLoaded(true);
  }, [gameId]);

  // Initial load + identity reset on game change.
  useEffect(() => {
    if (gameId === null) {
      setThemes([]);
      setChannels([]);
      setSummary(EMPTY_SUMMARY);
      setLoaded(false);
      return;
    }
    setLoaded(false);
    void refresh();
  }, [gameId, refresh]);

  // Realtime: debounce-coalesce multiple broadcasts in a tight window
  // into one refetch. 500ms matches the inbox realtime rule of thumb.
  useEffect(() => {
    if (gameId === null || version === 0) return;
    const handle = setTimeout(() => void refresh(), 500);
    return () => clearTimeout(handle);
  }, [gameId, version, refresh]);

  useEffect(() => {
    if (gameId === null) return;
    const id = window.setInterval(() => setTick((t) => t + 1), 30_000);
    return () => window.clearInterval(id);
  }, [gameId]);

  const handleRefresh = useCallback(async (): Promise<void> => {
    if (gameId === null || refreshing) return;
    setRefreshing(true);
    try {
      // Fan out to every watched channel, ignoring cooldown_active as an
      // expected race (a channel just completed elsewhere). Other errors
      // are swallowed for the moment - we'll surface them via the per-
      // channel sub-page on /insights/channels when needed.
      // In parallel, kick the non-Discord theme-formation pass so form /
      // playtest aspects also get a chance to graduate into themes from
      // this one button.
      const ineligible = channels.filter((c) => c.inFlight);
      const targets = channels.filter((c) => !ineligible.includes(c));
      await Promise.all([
        ...targets.map((c) => requestChannelSnapshot(c.discordChannelId, gameId)),
        requestNonDiscordSnapshot(gameId),
      ]);
      await refresh();
    } finally {
      setRefreshing(false);
    }
  }, [gameId, refreshing, channels, refresh]);

  if (activeGame === null) {
    return (
      <div className="w-full px-4 py-6 sm:px-6 sm:py-10">
        <p className="text-sm text-text-muted">
          Pick a game to view its insights.
        </p>
      </div>
    );
  }

  const lastRefresh = latestRefreshAt(channels);
  const nextDue = soonestNextDueAt(channels, Date.now());
  const nextDueMs = nextDue !== null ? nextDue - Date.now() : null;
  const refreshMetaParts: string[] = [];
  if (lastRefresh !== null) {
    refreshMetaParts.push(`last refresh ${relativeTime(lastRefresh)}`);
  }
  if (nextDueMs !== null) {
    refreshMetaParts.push(
      nextDueMs <= 0 ? 'next due now' : `next due in ${compactDuration(nextDueMs)}`,
    );
  }
  const refreshMeta = refreshMetaParts.join(' · ');

  // Any in-flight or local fan-out keeps the icon spinning so the user
  // knows the bot is still working. Stops once both go quiet.
  const isSpinning = refreshing || channels.some((c) => c.inFlight);

  return (
    <div className="w-full space-y-6 px-4 py-6 sm:px-6 sm:py-10">
      <InsightsStatusBanner />
      <section
        aria-label="Patterns"
        className="rounded-md border border-border bg-surface-2"
      >
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3 sm:px-6">
          <h2 className="text-sm text-text">
            {loaded ? (
              <PatternsHeadline
                patternCount={themes.length}
                summary={summary}
                channels={channels}
              />
            ) : (
              'Detecting patterns…'
            )}
          </h2>
          <div className="flex items-center gap-2 text-xs text-text-muted">
            {refreshMeta !== '' && <span>{refreshMeta}</span>}
            <button
              type="button"
              onClick={() => void handleRefresh()}
              disabled={refreshing || gameId === null}
              aria-label="Refresh insights"
              title="Refresh insights"
              className="rounded p-1 text-text-muted transition-colors hover:bg-surface-3 hover:text-text disabled:opacity-50"
            >
              <RefreshIcon
                className={isSpinning ? 'motion-safe:animate-spin' : undefined}
              />
            </button>
          </div>
        </header>
        <div className="p-4 sm:p-5">
          {loaded ? (
            <ThemesList
              themes={themes}
              totalMessages={summary.totalMessageCount}
              studioShortId={activeStudio.shortId}
              gameShortId={activeGame.shortId}
              onChanged={() => void refresh()}
            />
          ) : (
            <p className="text-sm text-text-muted">Loading patterns…</p>
          )}
        </div>
      </section>
    </div>
  );
}
